import { Component, Input, OnInit } from '@angular/core';
import { WorkflowApproverView } from 'app/models/form/WorkflowApprover';
import { ConditionField, ConditionOperator } from 'app/models/WorkflowGroupDto';

@Component({
	selector: 'client-approvers-list',
	templateUrl: './approvers-list.component.html',
	styleUrls: ['./approvers-list.component.css'],
	standalone: false,
})
export class ApproversListComponent implements OnInit {
	@Input() allApprovers: WorkflowApproverView[];
	@Input() currentApprovers: WorkflowApproverView[];
	@Input() hideHeading: boolean;
	@Input() hideCurrentApproveColumn: boolean;

	readonly WorkflowType = {
		RuleBased: 6,
	};

	readonly ConditionField = ConditionField;
	readonly ConditionOperator = ConditionOperator;

	constructor() {}

	ngOnInit(): void {}

	isNextApprover(workflowApprover: WorkflowApproverView) {
		if (this.currentApprovers && this.currentApprovers.length > 0) {
			const item = this.currentApprovers.find(
				(t) => t.userId == workflowApprover.userId && t.workflowStep == workflowApprover.workflowStep
			);
			return item != undefined;
		}
		return false;
	}

	isRuleBasedGroup(approver: WorkflowApproverView): boolean {
		return approver.workflowType === this.WorkflowType.RuleBased;
	}

	getMatchedRuleInfo(approver: WorkflowApproverView): any {
		if (approver.matchedRuleInfo) {
			try {
				return JSON.parse(approver.matchedRuleInfo);
			} catch (e) {
				return null;
			}
		}
		return null;
	}

	formatCondition(condition: any): string {
		if (!condition) return '';

		const field = condition.Field || condition.field || '';
		const operator = condition.Operator || condition.operator || '';
		const value = condition.Value ?? condition.value;
		const valueDisplay = condition.ValueDisplay ?? condition.valueDisplay;
		const lowValue = condition.LowValue ?? condition.lowValue;
		const highValue = condition.HighValue ?? condition.highValue;

		const fieldDisplay = this.getFieldDisplayName(field);
		const isThresholdField = field === 'ThresholdValue' || field === 'Amount';
		const isBetweenOp = operator === 'Between' || operator === 6;
		const showBetween = isThresholdField && isBetweenOp && (lowValue != null || highValue != null);

		const operatorText = showBetween ? this.getOperatorText('Between') : this.getOperatorTextForField(operator, field);

		if (showBetween) {
			const low = lowValue != null ? Number(lowValue) : '';
			const high = highValue != null ? Number(highValue) : '';
			return `${fieldDisplay} ${operatorText} ${low} and ${high}`;
		}

		const displayValue = valueDisplay != null && valueDisplay !== '' ? valueDisplay : value ?? '';
		return `${fieldDisplay} ${operatorText} ${displayValue}`;
	}

	getOperatorText(operator: string | number): string {
		const operatorMap: { [key: string]: string } = {
			Equals: 'is equal to',
			NotEquals: 'is not equal to',
			GreaterThan: 'is greater than',
			GreaterThanOrEqual: 'is greater than or equal to',
			LessThan: 'is less than',
			LessThanOrEqual: 'is less than or equal to',
			Between: 'is between',
			Contains: 'contains',
			NotContains: 'does not contain',
			'0': 'is equal to',
			'1': 'is not equal to',
			'2': 'is greater than',
			'3': 'is greater than or equal to',
			'4': 'is less than',
			'5': 'is less than or equal to',
			'6': 'is between',
			'7': 'contains',
			'8': 'does not contain',
		};
		const key = typeof operator === 'number' ? String(operator) : operator;
		return operatorMap[key] ?? operatorMap[operator as string] ?? String(operator);
	}

	getOperatorTextForField(operator: string | number, field: string): string {
		const text = this.getOperatorText(operator);
		const isThresholdField = field === 'ThresholdValue' || field === 'Amount';
		const isBetween = operator === 'Between' || (typeof operator === 'number' && operator === 6);
		if (!isThresholdField && isBetween) {
			return 'is equal to';
		}
		return text;
	}

	getFieldDisplayName(field: string): string {
		const fieldMap: { [key: string]: string } = {
			Department: 'Department',
			Amount: 'Threshold Value',
			ThresholdValue: 'Threshold Value',
			SpecificUser: 'Specific User',
			Account: 'Account',
			Fund: 'Fund',
		};

		return fieldMap[field] || field;
	}
}
